import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext } from 'react';
import { S_ErrorFeedback } from '../../../../design/feedback/errorFeedback/S_ErrorFeedback';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { DecryptionScriptSampleContainer } from '../../../containers/DecryptionScriptSample.container';
import { EnableFor } from '../../../containers/EnableForRole';
import { Plan } from '../../user/user.store';
import { UpdateDatasetContainer } from '../containers/UpdateDataset.container';
import { DatasetDetails } from '../DatasetDetails.context';
import { useDatasetPermissions } from '../datasets.permissions';
import { IDatasetDetails } from '../datasetsDetails.store';

interface IDatasetDetailsActionsProps {
  details: IDatasetDetails;
  isDeleteDisabled: boolean;
  isDatasetPublishable: boolean;
  isImageCountExceeded: boolean;
  availableImageCount: number;
  ownerPlan: Plan;
  onShowDatasetDeleteConfirmationModal: (datasetId: string) => void;
  onShowPublishDatasetConfirmationModal: () => void;
  onDownload: () => Promise<void>;
}

export const DatasetDetailsActions = observer((props: IDatasetDetailsActionsProps): ReactElement => {
  const { t } = useTranslator();
  const datasetsPermissions = useDatasetPermissions();
  const context = useContext(DatasetDetails);

  const canPublishDataset = datasetsPermissions.canPublishDataset();
  const canDownloadDataset = datasetsPermissions.canDownloadDataset();
  const canDeleteDataset = datasetsPermissions.canDeleteDataset(context.store.details.status, context.store.details.createdById);

  const getPublishButtonTitle = (): string | undefined => {
    if (props.details.status === 'Uploading') {
      return t('during_upload_publish_is_not_possible');
    }
    if (props.details.imagesCount === 0) {
      return t('publishing_empty_dataset_is_not_possible');
    }
    return undefined;
  };

  const showDeleteModal = (): void => {
    props.onShowDatasetDeleteConfirmationModal('');
  };

  return (
    <ColumnLayout size='sm'>
      <UpdateDatasetContainer />
      {props.details.status === 'Draft' || props.details.status === 'Uploading' ? (
        <EnableFor hasPermission={canPublishDataset}>
          <Button
            disabled={!props.isDatasetPublishable}
            onClick={props.onShowPublishDatasetConfirmationModal}
            title={getPublishButtonTitle()}
          >
            {t('publish_dataset')}
          </Button>
        </EnableFor>
      ) : (
        <></>
      )}
      <EnableFor hasPermission={canDownloadDataset}>
        <Button
          disabled={!props.details.canDownload}
          onClick={() => {
            void props.onDownload();
          }}
        >
          {t('download_dataset')}
        </Button>
      </EnableFor>
      <DecryptionScriptSampleContainer />
      <EnableFor hasPermission={canDeleteDataset}>
        <Button
          disabled={props.isDeleteDisabled}
          variant='outlined'
          onClick={showDeleteModal}
          title={props.isDeleteDisabled ? t('delete_error_dataset_assigned_to_project') : ''}
        >
          {t('delete_dataset')}
        </Button>
      </EnableFor>
      {props.isImageCountExceeded && (
        <S_ErrorFeedback>
          {t('publishing_dataset_will_exceed_workspace_owners_total_files_limit', {
            availableImageCount: props.availableImageCount,
            plan: t(props.ownerPlan),
          })}
        </S_ErrorFeedback>
      )}
    </ColumnLayout>
  );
});
